import {
    type HierarcicalObjectReference,
    type ObjectData,
    type ObjectDB,
    type ObjectId,
    type SearchOptions,
} from "@novorender/data-js-api";

import { getProcoreObjectMetadata } from "./bimObjectMetadata";
import { procoreResolveInstanceIdsToObjectIds, procoreResolveObjectIdsToInstanceIds } from "./bimObjectResolve";
import { searchProcoreObjects } from "./bimObjectSearch";
import { createBimSignedDownloadUrl, rewriteSignedUrlForProxy } from "./bimSignedDownloadUrl";

/**
 * ObjectDB implementation for Procore BIM Search DB API.
 *
 * Provides object metadata, search, and hierarchy queries by calling
 * the Procore BIM Search DB endpoints.
 */
export class ProcoreObjectDB implements ObjectDB {
    constructor(
        private readonly companyId: number,
        private readonly projectId: number,
        private readonly sceneId: string,
        private readonly sceneVersionId: string,
    ) {}

    async loadCompleteMetadata(id: number): Promise<ObjectData> {
        return this.getObjectMetdata(id);
    }

    async getObjectMetdata(id: number): Promise<ObjectData> {
        return getProcoreObjectMetadata({
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            objectId: id,
        });
    }

    async *search(
        filter: SearchOptions,
        signal: AbortSignal | undefined,
    ): AsyncIterableIterator<HierarcicalObjectReference> {
        yield* searchProcoreObjects({
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            filter,
            signal,
        });
    }

    async descendants(object: HierarcicalObjectReference, signal: AbortSignal | undefined): Promise<ObjectId[]> {
        try {
            // Fast path: Try to fetch pre-computed descendants file
            // These files are available via signed download URL with file=descendants/{objectId}
            const signedUrl = await createBimSignedDownloadUrl({
                companyId: this.companyId,
                projectId: this.projectId,
                sceneId: this.sceneId,
                sceneVersionId: this.sceneVersionId,
            });

            // Rewrite for proxy if needed (local development)
            const rewrittenUrl = rewriteSignedUrlForProxy(signedUrl);

            // Construct URL with file parameter for descendants file
            // The signed URL format is: {baseUrl}?companyId=X&projectId=Y&expiresAt=Z&signature=ABC&file=...
            // We need to append &file=descendants/{objectId} to the query string
            const separator = rewrittenUrl.includes("?") ? "&" : "?";
            const descendantsUrl = `${rewrittenUrl}${separator}file=descendants/${object.id}`;

            // Fetch the descendants file (newline-delimited IDs)
            const response = await fetch(descendantsUrl, { signal });

            if (!response.ok) {
                throw new Error(`Failed to fetch descendants file: ${response.status}`);
            }

            const text = await response.text();

            // Parse newline-delimited IDs
            return text
                .trim()
                .split("\n")
                .map((line) => parseInt(line.trim(), 10))
                .filter((id) => !isNaN(id));
        } catch {
            // Fallback: Use search endpoint if descendants file doesn't exist or fetch fails
            // This handles cases where the file might not be available for all objects
            const ids: ObjectId[] = [];

            for await (const result of searchProcoreObjects({
                companyId: this.companyId,
                projectId: this.projectId,
                sceneId: this.sceneId,
                sceneVersionId: this.sceneVersionId,
                filter: {
                    parentPath: object.path,
                    descentDepth: undefined, // undefined = all descendants
                },
                signal,
            })) {
                ids.push(result.id);
            }

            return ids;
        }
    }

    async resolveObjectIdsToInstanceIds(
        objectIds: ObjectId[],
        signal: AbortSignal | undefined,
    ): Promise<(string | null)[]> {
        return procoreResolveObjectIdsToInstanceIds({
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            objectIds,
            signal,
        });
    }

    async resolveInstanceIdsToObjectIds(
        instanceIds: string[],
        signal: AbortSignal | undefined,
    ): Promise<(ObjectId | null)[]> {
        return procoreResolveInstanceIdsToObjectIds({
            companyId: this.companyId,
            projectId: this.projectId,
            sceneId: this.sceneId,
            sceneVersionId: this.sceneVersionId,
            instanceIds,
            signal,
        });
    }
}
